import { useCallback, useState } from 'react';

import type { GridRowSelectionModel } from '@mui/x-data-grid';

const buildSelectAllModel = (
  rowCount: number,
  excludedIndices?: ReadonlySet<number>,
): GridRowSelectionModel => ({
  type: 'include',
  ids: new Set(
    Array.from({ length: rowCount }, (_value, index) => index).filter(
      (index) => !excludedIndices?.has(index),
    ),
  ),
});

const resolveSelectedIds = (
  model: Readonly<GridRowSelectionModel>,
  rowCount: number,
): ReadonlySet<number> =>
  model.type === 'exclude'
    ? new Set(
        Array.from({ length: rowCount }, (_value, index) => index).filter(
          (index) => !model.ids.has(index),
        ),
      )
    : (model.ids as Set<number>);

interface Params<T> {
  readonly excludedIndices?: ReadonlySet<number>;
  readonly items: readonly T[];
}

interface Result {
  readonly resolveSelectedIndices: () => ReadonlySet<number>;
  readonly rowSelectionModel: GridRowSelectionModel;
  readonly selectedCount: number;
  readonly setRowSelectionModel: (model: GridRowSelectionModel) => void;
}

export const useSelectAllRowSelectionModel = <T,>({
  excludedIndices,
  items,
}: Readonly<Params<T>>): Result => {
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>(() =>
      buildSelectAllModel(items.length, excludedIndices),
    );

  const [previousItems, setPreviousItems] = useState(items);
  if (items !== previousItems) {
    setPreviousItems(items);
    setRowSelectionModel(buildSelectAllModel(items.length, excludedIndices));
  }

  const resolveSelectedIndices = useCallback(
    () => resolveSelectedIds(rowSelectionModel, items.length),
    [items.length, rowSelectionModel],
  );

  const selectedCount = Array.from(resolveSelectedIndices()).filter(
    (index) => !excludedIndices?.has(index),
  ).length;

  return {
    resolveSelectedIndices,
    rowSelectionModel,
    selectedCount,
    setRowSelectionModel,
  };
};
